/**
 * Entering and leaving the sample-student view a nonprofit demo teacher uses
 * to show both halves of the product from one login.
 *
 * The switch itself is server-side: the endpoints set and clear an HttpOnly
 * cookie, and every later request is answered as the sample student. Nothing
 * here fakes an identity, so the client's only jobs are (a) drop per-identity
 * browser state that would otherwise follow the teacher into the other view,
 * and (b) hard-navigate.
 *
 * The navigation is deliberately a full document load rather than a router
 * push: the reload rebuilds the QueryClient and every user-keyed cache, which
 * is what makes the swap total. It happens only after a 2xx, and only from a
 * click — never from an effect. A cookie the server declines to honour simply
 * reports `active: false` on the next role fetch, so there is no reload loop.
 *
 * Update when the switch endpoints, their error codes, or the landing pages on
 * either side of the switch change.
 */

import { STUDENT_HOME } from '@/config/studentRoutes';
import { ADAPTIVE_SESSION_STORAGE_KEY } from '@/hooks/usePracticeSession';
import { userApi } from '@/services/userApi';
import { markLandingReload } from '@/utils/teacherEventSource';

/**
 * Where leaving the sample-student view lands. `/teacher/students` is what
 * `getDefaultRedirectPath` resolves for a teacher whose team policy hides the
 * default teacher landing — the adaptive-only nonprofit case this feature
 * exists for — so both paths agree on one teacher home.
 */
export const TEACHER_VIEW_HOME = '/teacher/students';

/** Server code for "this account has no sample student to view as". */
const DEMO_PERSONA_MISSING = 'demo_persona_missing';

type AccessTokenReader = () => Promise<string>;

/**
 * Drop the resumable adaptive session id. It is keyed per browser, not per
 * user, so leaving it in place would offer the other identity a session it
 * cannot rehydrate.
 */
const clearResumableSession = (): void => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(ADAPTIVE_SESSION_STORAGE_KEY);
  } catch {
    // A blocked storage API is not a reason to abandon a completed switch.
  }
};

const reloadInto = (path: string): void => {
  if (typeof window === 'undefined') return;
  window.location.assign(path);
};

/** Begin answering as the linked sample student and land on their home page. */
export const enterDemoStudentView = async (
  getAccessToken: AccessTokenReader
): Promise<void> => {
  const token = await getAccessToken();
  await userApi.enterDemoStudentView(token);
  clearResumableSession();
  reloadInto(STUDENT_HOME);
};

/** Return to the signed-in teacher's own account and land on their roster. */
export const exitDemoStudentView = async (
  getAccessToken: AccessTokenReader
): Promise<void> => {
  const token = await getAccessToken();
  await userApi.exitDemoStudentView(token);
  clearResumableSession();
  // A full document load carries no router state, so the Students class page
  // learns from a one-shot marker that this open was a landing, not a choice.
  markLandingReload();
  reloadInto(TEACHER_VIEW_HOME);
};

/**
 * Failure copy for the switch control. Only the one case a teacher can act on
 * gets its own sentence, and it names who sets a sample student up; everything
 * else is a retry, because no other failure tells them anything they can use.
 */
export const describeDemoViewError = (err: unknown): string =>
  (err as { code?: unknown } | null | undefined)?.code === DEMO_PERSONA_MISSING
    ? 'No sample student is set up for you. Ask DeepThink Admin to set one up.'
    : 'Couldn’t switch view. Try again.';
